// CONTRACTUL SOLUȚIILOR PE FAZE, de partea interfeței — și singurul loc din UI
// care vorbește cu registrul (contractul API «soluțiile pe faze», 25.09.2026,
// peste `structure/docs/plan_trei_etape.md` §3, §4, §7 și
// `ui/docs/arhitectura_informatiei.md`).
//
// Aici stau:
//
//   * tipurile contractului, scrise câmp cu câmp ca în el — `SolutieRand`
//     înlocuiește vechiul `api.Solution`, `RegistruSolutii` lista plată;
//   * ADAPTORUL (`citesteRegistru`): serverul care implementează contractul se
//     construiește în paralel, pe altă ramură. Cât îi lipsește un câmp, aici
//     se completează — `stare` din tabelul contractului, `implicit` din regula
//     lui (v. `regulaImplicita`) — și NUMAI aici. Restul interfeței citește
//     forma contractului și nu știe că a existat alta;
//   * rutele noi: registrul cu ETag, soluția implicită, cascada, ștergerea
//     unei soluții, pornirea pe fază.
//
// Nu se reimplementează nimic din ce decide serverul: regula soluției
// implicite e a lui (Î22), iar `regulaImplicita` e numai oglinda ei pentru
// răspunsurile care încă nu poartă `implicit`.

import { ApiError, del, get, post, type Job, type MotivAsteptare, type ParametriCod,
         type Silueta, type Sterse } from "../api";
import type { CategoriePicata } from "../picate";
import type { ScorParti } from "../scor";
import { t } from "../i18n";
import { arata as arataToast } from "../toasts";

// --- tipurile contractului ---------------------------------------------------

export type Disciplina = "site_plan" | "architecture" | "structure";
export type Faza = "schita" | "macro" | "detaliu";
export type Sursa = "generat" | "om" | "structurist";
export type StatusSolutie =
  "queued" | "running" | "ready" | "failed" | "nonconvergent" | "abandoned";
/** Starea pe care o desenează UI-ul, calculată de server (contract §3). */
export type Stare =
  "gata" | "nu_trece" | "in_lucru" | "la_rand" | "asteapta" | "picata" | "oprita";

export const DISCIPLINE: readonly Disciplina[] = ["site_plan", "architecture", "structure"];

export interface RegistruSolutii {
  solutii: SolutieRand[];
  /** joburi deschise care încă n-au rând: arhitectura înainte de pasul 1,
   *  planul de situație. Din ele desenează UI-ul fantomele. */
  rulari_fara_solutie: Rulare[];
  implicit: Implicit;
  generat_la: string;
}

/** Rezumatul de azi al planului de situație și al arhitecturii — «ca azi»,
 *  spune contractul. La structură e `{}`: acolo se citește `structura`. */
export type RaportCirculatie = {
  strategie?: string; strategie_nume?: string; parcare?: string; parcare_motiv?: string;
  locuri_sol?: number; locuri_subsol?: number; locuri_subsol_reale?: boolean;
  apartamente?: number; necesar?: number; tinta_sol?: number;
  verde_total_mp?: number; verde_pe_sol_mp?: number;
  rampe?: number; rampe_legate?: number; rampe_nelegate?: string[];
  componente?: number; fezabil?: boolean;
  verificari?: { nume: string; realizat: string; cerinta: string; ok: boolean }[];
  note?: string[];
  incercari?: { eticheta: string; strategie?: string; parcare?: string; locuri_sol?: number;
                fezabil?: boolean; aplicabila: boolean; motiv?: string; castigatoare?: boolean;
                /** «curburi» (planșa care concurează) sau «fara_curburi» (aceeași rețea, colțurile
                 *  intersecțiilor nefiletate — doar pe ecranul de încercări) */
                varianta?: "curburi" | "fara_curburi";
                /** pe varianta fără curburi: locuri la sol față de sora ei cu curburi */
                locuri_pierdute_curburi?: number | null }[];
  [key: string]: unknown;
};

export type RezumatSolutie = {
  /** devizul fațadei, pe scurt: prețuit de serviciul de planificare din metrajul
   *  arhitecturii și scris de control plane (`services/cost_fatada.py`) */
  cost_fatada?: { direct_eur: number; total_eur: number; eur_pe_m2: number | null;
                  precizie: string; corpuri: Record<string, number> } | null;
  checks_failed?: number;
  checks_total?: number;
  picate_categorii?: CategoriePicata[];
  headline?: { label: string; value: number | null; unit: string };
  apartamente?: number | null;
  niveluri?: number | null;
  locuri_parcare?: number | null;
  fezabil?: boolean;
  necontrolat?: boolean;
  abateri?: { titlu: string; valoare: number | null; prag: number | null;
              um: string | null; articol: string | null }[];
  scor?: number | null;
  scor_parti?: ScorParti | null;
  silueta?: Silueta | null;
  parcare?: { livrat: number | null; necesar: number | null;
              subsol: number | null; sol: number | null;
              deficit_subsol?: number } | null;
  rol?: string | null;
  eticheta?: string | null;
  /** raportul pasului 2 al planului (`positioning_plan/backend/circulatie._raport`),
   *  pe soluție și pe încercări: statisticile de pe card */
  circulatie?: RaportCirculatie | null;
  /** editarea pe loc a unui nivel (contract §5.6) */
  editat?: { la?: string | null } | null;
  /** PLANUL DE SITUAȚIE: circulația lui s-a trasat după scările de evacuare ale
   *  altei arhitecturi decât cea ★ de acum (`services/faze.circulatia_veche_dupa_promovare`);
   *  «Refă planul cu agentul» o aduce la zi. `scari` = câte scări la teren are ★. */
  circulatie_veche?: { motiv: string; arhitectura: string; la: string; scari: number } | null;
  /** ARHITECTURA IMPORTATĂ: ce structură au desenată planșele ei — stâlpii,
   *  pereții de beton și grinzile de pe layerele de structură
   *  (`control-plane/services/structura_desenata.py`). De aici știe «Importă
   *  structura» dacă are ce importa. Lipsă pe arhitecturile generate. */
  structura_desenata?: StructuraDesenata | null;
  [key: string]: unknown;
};

export type StructuraDesenata = { v: number; stalpi: number; pereti: number; grinzi: number };

export interface SolutieRand {
  id: string;
  discipline: Disciplina;
  numar: number;
  /** doar structure */
  faza: Faza | null;
  /** doar structure */
  sursa: Sursa | null;
  /** Numele pentru jurnal și cel dat de generator (importuri). Numele
   *  AFIȘAT se compune din `discipline` + `numar` + `faza` (v.
   *  `nav/selectie.numeRand`). */
  label: string;
  status: StatusSolutie;
  stare: Stare;
  /** de ce e picată sau oprită; la «nu trece», textul blocajului */
  motiv: string | null;
  /** mereu false la structure */
  promoted: boolean;
  promoted_by: string | null;
  in_lant: boolean;
  parent_solution_id: string | null;
  lineage: { site_plan?: string; architecture?: string };
  /** jobul deschis al rândului */
  rulare: Rulare | null;
  /** doar site_plan și architecture */
  faze: FazaEtapa[] | null;
  /** doar architecture: ce scrie pe ea după etaje (lipsă într-un răspuns
   *  de dinainte de câmp) */
  lucrari?: Lucrare[] | null;
  /** doar la o arhitectură IMPORTATĂ, gata: planșele citite de import față de
   *  dosarul de acum (`control-plane/services/planse_import.py`); lipsă sau
   *  `null` la orice altceva */
  planse?: PlanseImport | null;
  summary: RezumatSolutie;
  /** doar structure */
  structura: DetaliiStructura | null;
  /** intern, nu se afișează */
  version: number;
  created_at: string;
  updated_at: string | null;
  finished_at: string | null;
}

/** O planșă de nivel citită de import: fișierul și nivelurile ieșite din el. */
export interface PlansaImport {
  fisier_id: string;
  fisier: string | null;
  niveluri: string[];
}

export interface PlanseImport {
  /** a ieșit din planșe care nu mai sunt în dosar, sau a apărut un plan nou */
  schimbate: boolean;
  citite: PlansaImport[];
  /** …dintre ele, cele șterse din dosar */
  lipsa: PlansaImport[];
  /** planurile de nivel urcate după pornirea importului */
  noi: { fisier_id: string; fisier: string; urcat_la: string }[];
}

export interface FazaEtapa extends MotivAsteptare {
  /** Planul de situație are două faze: `amplasare` (pasul 1) și `agent` —
   *  agentul care desenează circulația, locurile, trotuarele și zonele (jobul
   *  `site_plan_agent`). Pasul 2 al planului e doar pregătirea pentru agent
   *  (secunde), deci n-are fază a lui: stările pregătirii (`asteapta` cât
   *  așteaptă arhitectura, `picata` cu motiv) le poartă faza `agent`. */
  id: "amplasare" | "agent" | "etaje" | "subsol" | "import" | "recomp" | "fatada";
  stare: Stare | "nepornita";
  motiv: string | null;
}

/** O lucrare care scrie ÎN LOC pe o arhitectură cu etajele gata: ultima
 *  rulare a felului ei. Rândul rămâne «gata» cât rulează (se deschide). */
export interface Lucrare {
  kind: "basement_architecture" | "recomp" | "facade";
  job_id: string;
  stare: Stare;
  /** jobul, cât e deschis */
  rulare: Rulare | null;
  /** de ce a picat */
  motiv: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** încercările picate de după ultima reușită, înaintea celei de acum */
  incercari_picate: number;
}

export interface DetaliiStructura {
  /** M și D încheiate; null la S0 și la M editată */
  poarta: Poarta | null;
  cifre: CifreStructura;
  corpuri: CorpRand[];
  /** doar sursa = "om" */
  editare: {
    din: string; blocate: number;
    /** numărul soluției generate din care coboară (0 = schița) și a câta
     *  editare e pe ea — «Structură 3 · Editare 2» */
    radacina?: number; nr?: number;
  } | null;
  /** ⚠ «calculată pe arhitectura dinainte de editare» */
  arhitectura_editata_la: string | null;
  /** doar la detaliu ready */
  cost: Cost | null;
}

export interface Poarta {
  trece: boolean;
  checks_blocking: number | null;
  rafinata: boolean;
  verificari: VerificarePoarta[];
}

export interface VerificarePoarta {
  rule_code: string;
  rol: "blocant" | "high";
  verdict: "conform" | "neconform" | "de_confirmat" | "inaplicabil" | "neverificabil";
  directie: "X" | "Y" | null;
  valoare: number | null;
  limita: number | null;
  prezis: number | null;
  unitate: string | null;
  sursa_limitei: "preferinta" | "arhitectura" | "llm_guess" | "default" | null;
}

export interface CifreStructura {
  headline: { label: string; value: number | null; unit: string } | null;
  scor: number | null;
  checks_total: number | null;
  checks_failed: number | null;
  /** separări ale perioadelor între 10 și 15 %: nu picate, «de revizuit» */
  checks_review?: number | null;
  /** critice și globale picate, cum le arată ecranul de verificări (fără
   *  banda «de revizuit»): bulina de pe «Verificări» din șină. Lipsă pe
   *  rândurile de dinainte de câmp — atunci nu se desenează nimic */
  checks_failed_global?: number | null;
  picate_categorii: CategoriePicata[] | null;
  tipuri_esv: Record<string, number> | null;
  esv_pe_nivel: Record<string, number> | null;
  fundare: Record<string, { sistem: string; h_r_m: number | null }> | null;
}

export interface CorpRand {
  /** «C1», …; «ansamblu» cu ansamblu = true */
  cod: string;
  /** uuid5(soluție, cod): de aici se citesc pașii */
  spatiu: string;
  ansamblu: boolean;
  stare: Stare | "sarit" | "exclus";
  motiv: string | null;
  /** critice și globale picate PE CORPUL ăsta (bulina din șină pe pagina
   *  lui); lipsă pe rândurile de dinainte de câmp */
  checks_failed_global?: number | null;
}

export interface Cost { direct_eur: number | null; total_eur: number | null }

export interface Rulare {
  job_id: string;
  kind: string;
  faza: Faza | null;
  solution_id: string | null;
  upstream_solution_id: string | null;
  status: "pending" | "running" | "waiting";
  stage: string | null;
  stage_cod?: string | null;
  stage_param?: ParametriCod | null;
  step_index: number | null;
  step_total: number | null;
  /** pasul CERUT jobului: 2 după reluarea din `waiting`, cu `step_index` încă
   *  al pasului 1 (v. `control-plane/api/jobs.py`, reluarea) */
  step?: number | null;
  /** pasul în lucru; titlul vine cu codul lui (v. `titluPasului`) */
  pas: { key: string; titlu: string | null; titlu_cod?: string | null;
         titlu_param?: ParametriCod | null } | null;
  macro: ProgresMacro | null;
  asteapta: { kind: string; job_id: string | null;
              upstream_solution_id: string | null } & MotivAsteptare | null;
  oprire_ceruta: boolean;
  is_dead: boolean;
  eta_s: number | null;
  inlantuire: string | null;
  started_at: string | null;
  heartbeat_at: string | null;
}

export interface ProgresMacro {
  etapa: "bucla" | "conformare" | "rafinare" | "finalizare";
  trecere: number | null;
  treceri_max: number | null;
  acum: string | null;
  treceri: { drift_x: number | null; drift_y: number | null;
             t1_s: number | null; dt_s: number }[];
}

export interface Implicit {
  /** firul pe care se deschide un proiect fără URL și fără «ultimul fir privit» */
  fir: { site_plan: string | null; architecture: string | null; structure: string | null };
  architecture_pe_site_plan: Record<string, string | null>;
  structure_pe_architecture: Record<string, string | null>;
}

export type MotivImplicit =
  "favorita" | "prima_gata" | "cea_mai_noua_solutie" | "schita" | "nimic";

export type PornireStructura =
  | { kind: "structure"; faza: "schita"; upstream_solution_id: string }
  /** «Generează structura»; cu `import_structura`, «Importă structura»: geometria
   *  macro se citește din planșe (`sursa = structurist`), lângă cea generată */
  | { kind: "structure"; faza: "macro"; upstream_solution_id: string; import_structura?: boolean }
  | { kind: "structure"; faza: "macro"; solution_id: string }
  | { kind: "structure"; faza: "detaliu"; parent_solution_id: string };

export interface PornireRaspuns extends Job {
  solutie: SolutieRand;
  urmeaza: "macro" | null;
  sters: Sterse;
}

export type ActiuneCascada = "sterge" | "schita" | "reruleaza" | "detaliu";

export interface Cascada {
  actiune: ActiuneCascada;
  ids: string[];
  pe_discipline: { site_plan: number; architecture: number; structure: number };
  /** «A2 + 4 structuri»: M+D = 1 soluție */
  structura: { schite: number; solutii: number };
  rulari_vii: number;
}

// --- adaptorul ----------------------------------------------------------------

/** Starea din `status`, după tabelul contractului (§3). O calculează serverul;
 *  aici numai cât un răspuns n-o poartă încă. */
export function stareDinStatus(status: StatusSolutie, rulare: Rulare | null,
                               poarta: Poarta | null, disciplina: Disciplina): Stare {
  switch (status) {
    case "ready": return poarta && poarta.trece === false ? "nu_trece" : "gata";
    case "nonconvergent": return "nu_trece";
    case "queued": return "la_rand";
    case "failed": return "picata";
    case "abandoned": return "oprita";
    case "running": {
      if (!rulare || rulare.status === "running") return "in_lucru";
      if (rulare.status === "pending") return "la_rand";
      // `waiting`: detaliul care cedează unei macro așteaptă un job al ACELEIAȘI
      // discipline (e la rând); arhitectura care așteaptă schița, sau planul
      // care așteaptă arhitectura, așteaptă ALTĂ disciplină
      const alta = disciplinaFelului(rulare.asteapta?.kind);
      return alta && alta !== disciplina ? "asteapta" : "la_rand";
    }
  }
  return "in_lucru";
}

/** Disciplina pe care o produce un fel de job; `null` pentru felurile care
 *  citesc INTRĂRI (citirea actelor, studiul geotehnic). */
export function disciplinaFelului(kind: string | null | undefined): Disciplina | null {
  switch (kind) {
    case "site_plan": return "site_plan";
    case "architecture":
    case "external_architecture":
    case "basement_architecture": return "architecture";
    case "structure": return "structure";
  }
  return null;
}

const text = (x: unknown): string | null => (typeof x === "string" && x ? x : null);
const numarSau = (x: unknown, alt: number): number =>
  typeof x === "number" && Number.isFinite(x) ? x : alt;

/** Un rând, cu câmpurile lipsă completate. Numai ce spune contractul că
 *  există: câmpurile vechi (`mod`, `fidelity`, `search_stopped`) nu se citesc. */
export function citesteRand(raw: Record<string, unknown>): SolutieRand {
  const discipline = raw.discipline as Disciplina;
  const summary = (raw.summary ?? {}) as RezumatSolutie;
  const rulare = (raw.rulare ?? null) as Rulare | null;
  const structura = discipline === "structure"
    ? ((raw.structura ?? null) as DetaliiStructura | null) : null;
  const status = (raw.status ?? "queued") as StatusSolutie;
  const version = numarSau(raw.version, 0);
  return {
    id: String(raw.id),
    discipline,
    numar: numarSau(raw.numar, version),
    faza: discipline === "structure" ? ((raw.faza ?? null) as Faza | null) : null,
    sursa: discipline === "structure" ? ((raw.sursa ?? null) as Sursa | null) : null,
    label: text(raw.label) ?? "",
    status,
    stare: (raw.stare as Stare | undefined)
      ?? stareDinStatus(status, rulare, structura?.poarta ?? null, discipline),
    motiv: text(raw.motiv) ?? text(summary.motiv),
    promoted: discipline === "structure" ? false : !!raw.promoted,
    promoted_by: text(raw.promoted_by) ?? text(summary.promoted_by),
    in_lant: !!raw.in_lant,
    parent_solution_id: text(raw.parent_solution_id),
    lineage: (raw.lineage ?? {}) as SolutieRand["lineage"],
    rulare,
    faze: (raw.faze ?? null) as FazaEtapa[] | null,
    lucrari: discipline === "architecture" ? ((raw.lucrari ?? []) as Lucrare[]) : null,
    summary,
    structura: structura && {
      poarta: structura.poarta ?? null,
      cifre: structura.cifre ?? {
        headline: null, scor: null, checks_total: null, checks_failed: null,
        picate_categorii: null, tipuri_esv: null, esv_pe_nivel: null, fundare: null,
      },
      corpuri: structura.corpuri ?? [],
      editare: structura.editare ?? null,
      arhitectura_editata_la: structura.arhitectura_editata_la ?? null,
      cost: structura.cost ?? null,
    },
    version,
    created_at: text(raw.created_at) ?? "",
    updated_at: text(raw.updated_at),
    finished_at: text(raw.finished_at),
  };
}

/** Răspunsul registrului, în forma contractului.
 *
 *  Primește și lista plată de dinaintea contractului — un server încă
 *  nemigrat —, dar numai ca să nu cadă ecranul: rândurile ei trec prin
 *  aceeași completare, iar `mep` iese (contractul nu-l mai are). */
export function citesteRegistru(raw: unknown): RegistruSolutii {
  const obiect = Array.isArray(raw) ? { solutii: raw } : ((raw ?? {}) as Record<string, unknown>);
  const solutii = ((obiect.solutii ?? []) as Record<string, unknown>[])
    .filter((s) => DISCIPLINE.includes(s.discipline as Disciplina))
    .map(citesteRand);
  return {
    solutii,
    rulari_fara_solutie: (obiect.rulari_fara_solutie ?? []) as Rulare[],
    implicit: (obiect.implicit as Implicit | undefined) ?? regulaImplicita(solutii),
    generat_la: text(obiect.generat_la) ?? new Date().toISOString(),
  };
}

/** OGLINDA regulii soluției implicite a serverului (contract §4). Se folosește
 *  NUMAI în `citesteRegistru`, pentru un răspuns care nu poartă `implicit`:
 *  interfața citește `registru.implicit`, nu această funcție. */
export function regulaImplicita(solutii: SolutieRand[]): Implicit {
  const gata = (s: SolutieRand) => s.status === "ready";
  const dupaNumar = (a: SolutieRand, b: SolutieRand) => a.numar - b.numar;

  const structuraPe = (arh: string): string | null => {
    const aici = solutii.filter((s) => s.discipline === "structure"
                                       && s.lineage.architecture === arh);
    const macro = aici.filter((s) => s.faza === "macro")
      .sort((a, b) => b.numar - a.numar);
    for (const m of macro) {
      const d = aici.find((s) => s.faza === "detaliu" && s.parent_solution_id === m.id);
      if (d && gata(d)) return d.id;
      if (gata(m)) return m.id;
    }
    return aici.find((s) => s.faza === "schita")?.id ?? null;
  };
  const arhitecturaPe = (sp: string): string | null => {
    const aici = solutii.filter((s) => s.discipline === "architecture"
                                       && s.lineage.site_plan === sp);
    return aici.find((s) => s.promoted)?.id
      ?? aici.filter(gata).sort(dupaNumar)[0]?.id ?? null;
  };

  const arhitecturi = solutii.filter((s) => s.discipline === "architecture");
  const planuri = solutii.filter((s) => s.discipline === "site_plan");
  const arhStea = arhitecturi.find((s) => s.promoted) ?? null;
  const spFir = arhStea?.lineage.site_plan
    ?? planuri.find((s) => s.promoted)?.id
    ?? planuri.filter(gata).sort(dupaNumar)[0]?.id ?? null;
  const arhFir = arhStea?.id ?? (spFir ? arhitecturaPe(spFir) : null);
  return {
    fir: {
      site_plan: spFir,
      architecture: arhFir,
      structure: arhFir ? structuraPe(arhFir) : null,
    },
    architecture_pe_site_plan: Object.fromEntries(
      planuri.map((p) => [p.id, arhitecturaPe(p.id)])),
    structure_pe_architecture: Object.fromEntries(
      arhitecturi.map((a) => [a.id, structuraPe(a.id)])),
  };
}

// --- rutele ------------------------------------------------------------------

/** Ultimul răspuns al registrului, pe adresă, cu ETag-ul lui: la 304 serverul
 *  n-a trimis nimic, iar valoarea e cea de aici — aceeași identitate, deci
 *  ecranele nu se redesenează pentru un sondaj care n-a adus nimic nou. */
const ultimul = new Map<string, { etag: string; valoare: RegistruSolutii }>();

/** `GET /api/projects/:id/solutions` (contract §3). `interne` aduce și
 *  încercările de rampă ale arhitecturii. Citirea e de FUNDAL (sondaj): un
 *  eșec nu ajunge toast decât dacă se cere `silent: false`. */
export async function listSolutions(
  projectId: string,
  opt: { interne?: boolean; silent?: boolean } = {},
): Promise<RegistruSolutii> {
  const url = `/api/projects/${projectId}/solutions${opt.interne ? "?interne=1" : ""}`;
  const tinut = ultimul.get(url);
  let r: Response;
  try {
    r = await fetch(url, {
      credentials: "same-origin",
      headers: tinut ? { "If-None-Match": tinut.etag } : undefined,
    });
  } catch (err) {
    if (opt.silent === false) arataToast(t("api.server_mut"));
    throw err;
  }
  if (r.status === 304 && tinut) return tinut.valoare;
  if (!r.ok) {
    const payload = await r.json().catch(() => null);
    if (r.status >= 500 && opt.silent === false) {
      arataToast(t("api.esec", { stare: r.status, metoda: "GET", cale: url }));
    }
    throw new ApiError(r.status, payload?.error ?? "unknown",
                       payload?.message ?? r.statusText, payload ?? undefined);
  }
  const valoare = citesteRegistru(await r.json());
  const etag = r.headers?.get?.("ETag");
  if (etag) ultimul.set(url, { etag, valoare });
  else ultimul.delete(url);
  return valoare;
}

/** Uită ETag-urile ținute — după o ștergere sau o pornire, următoarea citire
 *  trebuie să aducă registrul întreg, nu să se bazeze pe unul vechi. */
export function uitaRegistrul(): void {
  ultimul.clear();
}

/** `GET …/solutions/implicita` (contract §4): aceeași regulă ca
 *  `registru.implicit`, pentru o singură întrebare. */
export const solutiaImplicita = (projectId: string, discipline: Disciplina,
                                 pe?: string | null) => {
  const q = new URLSearchParams({ discipline });
  if (pe) q.set("pe", pe);
  return get<{ solution_id: string | null; motiv: MotivImplicit }>(
    `/api/projects/${projectId}/solutions/implicita?${q.toString()}`);
};

/** `GET …/solutions/:sid/cascada` (contract §5.3): ce ar lua o acțiune, fără
 *  să scrie nimic. */
export const cascada = (projectId: string, sid: string, actiune: ActiuneCascada) =>
  get<Cascada>(`/api/projects/${projectId}/solutions/${sid}/cascada?actiune=${actiune}`);

/** `DELETE …/solutions/:sid` (contract §5.3): rândul și tot ce stă sub el.
 *  Pe S0 și pe D singur serverul refuză (409), fiindcă veriga i-ar renaște. */
export const stergeSolutia = (projectId: string, sid: string) =>
  del<{ ok: boolean; ids: string[]; sters: Sterse }>(
    `/api/projects/${projectId}/solutions/${sid}`);

/** `POST /api/projects/:id/jobs` cu `kind: "structure"` (contract §5.1). */
export const pornesteStructura = (projectId: string, corp: PornireStructura) =>
  post<PornireRaspuns>(`/api/projects/${projectId}/jobs`, corp);
